/**
 * ProductModal - Modal principal de criação/edição de produto.
 * Extraído VERBATIM de ProductsContent.js (Fase 2 refactor).
 * Recebe estado/handlers via props do closure de ProductsContent.
 */
import React from 'react';
import {
  Boxes, Check, ChevronRight, Image as ImageIcon, Layers, Plus, PlusCircle,
  Sparkles, Trash2, Upload, X
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

const ProductModal = ({
  isModalOpen,
  showGalleryModal,
  showAIImageModal,
  showBatchAIModal,
  setIsModalOpen,
  editingProduct,
  handleSubmit,
  formData,
  setFormData,
  categories,
  openCreateCategoryModal,
  handleImageFileSelect,
  imagePreview,
  selectedImageFile,
  handleImageUpload,
  uploadingImage,
  openGalleryModal,
  openAIImageModal,
  showQuickGroup,
  setShowQuickGroup,
  quickGroup,
  setQuickGroup,
  preventEnterSubmit,
  quickGroupItems,
  handleQuickGroupItemChange,
  removeQuickGroupItemRow,
  addQuickGroupItemRow,
  resetQuickGroup,
  handleCreateQuickGroup,
  savingQuickGroup,
  additionalGroups,
}) => {
  return (
      <Dialog
        open={isModalOpen}
        onOpenChange={(open) => {
          // Não permitir o modal de produto fechar enquanto modais filhos estão abertos
          // (Galeria, IA ou Geração em Lote). Isso evita o "fecha junto" em certos navegadores.
          if (!open && (showGalleryModal || showAIImageModal || showBatchAIModal)) {
            return;
          }
          setIsModalOpen(open);
        }}
      >
        <DialogContent
          data-testid="product-modal"
          closeButtonTestId="product-modal-close-button"
          className="glass-modal grid-cols-1 w-full max-w-2xl overflow-x-hidden overflow-y-auto max-h-[90vh]"
          onPointerDownOutside={(e) => {
            if (showGalleryModal || showAIImageModal || showBatchAIModal) e.preventDefault();
          }}
          onFocusOutside={(e) => {
            if (showGalleryModal || showAIImageModal || showBatchAIModal) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (showGalleryModal || showAIImageModal || showBatchAIModal) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (showGalleryModal || showAIImageModal || showBatchAIModal) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {editingProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Nome - Campo grande para mobile */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Nome do Produto *
              </label>
              <input
                type="text"
                required
                className="glass-input w-full text-base py-3 px-4"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: Hambúrguer Artesanal"
                data-testid="product-name-input"
              />
            </div>

            {/* Preço e Categoria - Layout responsivo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-base font-medium text-gray-300 mb-2">
                  Preço (R$) *
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-base">
                    R$
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    required
                    className="glass-input w-full text-base py-3 pl-12 pr-4"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0.00"
                    inputMode="decimal"
                    data-testid="product-price-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-base font-medium text-gray-300 mb-2">
                  Categoria *
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                      required
                      className="glass-input w-full text-base py-3 px-4 pr-10 appearance-none cursor-pointer"
                      data-testid="product-category-select"
                    >
                      <option value="">Selecionar</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <ChevronRight className="absolute right-3 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400 w-5 h-5 pointer-events-none" />
                  </div>
                  <button
                    type="button"
                    onClick={openCreateCategoryModal}
                    className="glass-button-primary px-4 py-3 flex items-center justify-center"
                    title="Criar nova categoria"
                    data-testid="create-category-inline-button"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Clique no "+" para criar uma nova categoria rapidamente
                </p>
              </div>
            </div>

            {/* Ordem de Exibição - Campo opcional */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Ordem de Exibição (opcional)
              </label>
              <div className="flex gap-3 items-start">
                <input
                  type="number"
                  min="1"
                  className="glass-input w-32 text-base py-3 px-4"
                  value={formData.display_order}
                  onChange={(e) => setFormData({...formData, display_order: e.target.value})}
                  placeholder="Ex: 1"
                  data-testid="product-display-order-input"
                />
                <p className="text-xs text-gray-500 flex-1 pt-3">
                  💡 Produtos com ordem definida aparecem primeiro no menu. Sem ordem = ordenado por preço.
                </p>
              </div>
            </div>
            
            {/* Descrição - Textarea maior */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Descrição
              </label>
              <textarea
                className="glass-input w-full resize-none text-base py-3 px-4"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descreva os ingredientes, sabor, porção..."
                data-testid="product-description-input"
              />
            </div>
            
            {/* Imagem do Produto - Upload ou URL */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Imagem do Produto
              </label>
              
              {/* Upload Section */}
              <div className="space-y-3">
                {/* File Upload */}
                <div className="bg-gray-800/30 rounded-xl p-4 border-2 border-dashed border-gray-700 hover:border-gray-600 transition-colors">
                  <input
                    type="file"
                    id="product-image-file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageFileSelect}
                    className="hidden"
                    data-testid="product-image-file-input"
                  />
                  <label 
                    htmlFor="product-image-file" 
                    data-testid="product-image-upload-trigger"
                    className="flex flex-col items-center justify-center cursor-pointer py-3"
                  >
                    <Upload size={32} className="text-gray-400 mb-2" />
                    <span className="text-sm text-gray-300 font-medium mb-1">
                      Clique para fazer upload
                    </span>
                    <span className="text-xs text-gray-500">
                      JPG, PNG ou WEBP (máx. 5MB)
                    </span>
                  </label>
                </div>

                {/* Preview and Upload Button */}
                {(imagePreview || selectedImageFile) && (
                  <div className="space-y-2">
                    <div className="rounded-lg overflow-hidden border border-gray-700">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleImageUpload}
                      disabled={uploadingImage}
                      className="w-full glass-button-primary py-3 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="upload-image-button"
                    >
                      {uploadingImage ? (
                        <>
                          <div className="loading-spinner inline-block mr-2" style={{width: '16px', height: '16px'}}></div>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Upload size={18} className="mr-2" />
                          Confirmar Upload
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-700"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-gray-900 text-gray-500">ou use URL externa</span>
                  </div>
                </div>

                {/* URL Input */}
                <input
                  type="url"
                  className="glass-input w-full text-base py-3 px-4"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="Cole o link da imagem aqui"
                  data-testid="product-image-url-input"
                />

                {/* Botão discreto para galeria */}
                <button
                  type="button"
                  onClick={openGalleryModal}
                  className="w-full text-xs text-gray-500 hover:text-gray-300 py-2 flex items-center justify-center gap-1 transition-colors"
                  data-testid="open-gallery-button"
                >
                  <ImageIcon size={12} />
                  <span>Usar imagem da galeria</span>
                </button>

                {/* Botão discreto para gerar com IA */}
                <button
                  type="button"
                  onClick={openAIImageModal}
                  className="w-full text-xs text-gray-500 hover:text-purple-400 py-1 flex items-center justify-center gap-1 transition-colors"
                  data-testid="open-ai-image-button"
                >
                  <Sparkles size={12} />
                  <span>Gerar imagem com IA</span>
                </button>
                
                {/* Final Image Preview */}
                {formData.image_url && !imagePreview && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-gray-700">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-40 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
              
              <p className="text-xs text-gray-500 mt-2">
                💡 Dica: Imagens de alta qualidade melhoram a apresentação do produto
              </p>
            </div>
            
            {/* Tempo de Preparo - Campo único em mobile */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Tempo de Preparo
              </label>
              <div className="relative">
                <input
                  type="number"
                  className="glass-input w-full text-base py-3 px-4 pr-16"
                  value={formData.preparation_time}
                  onChange={(e) => setFormData({...formData, preparation_time: e.target.value})}
                  placeholder="15"
                  inputMode="numeric"
                  data-testid="product-prep-time-input"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-base">
                  min
                </span>
              </div>
            </div>

            {/* 📦 Controle de Estoque */}
            <div className="bg-gradient-to-br from-orange-500/5 to-red-500/5 border border-orange-500/20 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-base font-medium text-gray-200 flex items-center gap-2">
                    <Boxes className="w-4 h-4 text-orange-400" />
                    Controle de estoque
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Decremente automaticamente a cada pedido
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, track_stock: !formData.track_stock })}
                  data-testid="product-track-stock-toggle"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                    formData.track_stock ? 'bg-orange-500' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      formData.track_stock ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {formData.track_stock && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-orange-500/10">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Saldo atual</label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={formData.stock_quantity}
                      onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                      placeholder="0"
                      data-testid="product-stock-quantity"
                      className="glass-input w-full text-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Unidade</label>
                    <select
                      value={formData.stock_unit}
                      onChange={(e) => setFormData({ ...formData, stock_unit: e.target.value })}
                      data-testid="product-stock-unit"
                      className="glass-input w-full text-sm py-2 px-3"
                    >
                      {['un', 'kg', 'g', 'L', 'ml', 'dz', 'pct'].map((u) => (
                        <option key={u} value={u} className="bg-gray-800">{u}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Alerta abaixo de</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.low_stock_threshold}
                      onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                      placeholder="Padrão da loja"
                      data-testid="product-low-threshold"
                      className="glass-input w-full text-sm py-2 px-3"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Grupos de Adicionais */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <label className="block text-base font-medium text-gray-300">
                  Grupos de Adicionais
                </label>
                <button
                  type="button"
                  data-testid="quick-add-group-button"
                  onClick={() => setShowQuickGroup(v => !v)}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-blue-300 hover:text-blue-200 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg px-3 py-1.5 transition-colors"
                >
                  <PlusCircle size={14} />
                  Novo grupo
                </button>
              </div>

              {/* Painel embutido de criação rápida de grupo */}
              {showQuickGroup && (
                <div
                  data-testid="quick-group-panel"
                  className="mb-3 rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 space-y-3"
                >
                  <div className="flex items-center gap-2 text-blue-200">
                    <Layers size={16} className="flex-shrink-0" />
                    <span className="text-sm font-medium">Criar grupo e vincular ao produto</span>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Nome do grupo *</label>
                    <input
                      type="text"
                      data-testid="quick-group-name"
                      autoFocus
                      value={quickGroup.name}
                      onChange={(e) => setQuickGroup({ ...quickGroup, name: e.target.value })}
                      onKeyDown={preventEnterSubmit}
                      placeholder="Ex: Escolha o ponto da carne"
                      className="glass-input w-full text-sm py-2 px-3"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="min-w-0">
                      <label className="block text-xs font-medium text-gray-400 mb-1">Mín. escolhas</label>
                      <input
                        type="number"
                        min="0"
                        data-testid="quick-group-min"
                        value={quickGroup.min_choices}
                        onChange={(e) => setQuickGroup({ ...quickGroup, min_choices: e.target.value })}
                        onKeyDown={preventEnterSubmit}
                        className="glass-input w-full text-sm py-2 px-3"
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block text-xs font-medium text-gray-400 mb-1">Máx. escolhas</label>
                      <input
                        type="number"
                        min="1"
                        data-testid="quick-group-max"
                        value={quickGroup.max_choices}
                        onChange={(e) => setQuickGroup({ ...quickGroup, max_choices: e.target.value })}
                        onKeyDown={preventEnterSubmit}
                        className="glass-input w-full text-sm py-2 px-3"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 min-w-0 bg-gray-800/30 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-300 min-w-0">Obrigatório</span>
                    <Switch
                      data-testid="quick-group-required"
                      className="flex-shrink-0"
                      checked={quickGroup.is_required}
                      onCheckedChange={(checked) => setQuickGroup({ ...quickGroup, is_required: checked })}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-gray-400">Itens do grupo (opcional)</label>
                    {quickGroupItems.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 min-w-0">
                        <input
                          type="text"
                          data-testid={`quick-group-item-name-${i}`}
                          value={item.name}
                          onChange={(e) => handleQuickGroupItemChange(i, 'name', e.target.value)}
                          onKeyDown={preventEnterSubmit}
                          placeholder="Nome do item"
                          className="glass-input flex-1 min-w-0 text-sm py-2 px-3"
                        />
                        <div className="relative flex-shrink-0 w-24">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">R$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            data-testid={`quick-group-item-price-${i}`}
                            value={item.price}
                            onChange={(e) => handleQuickGroupItemChange(i, 'price', e.target.value)}
                            onKeyDown={preventEnterSubmit}
                            placeholder="0,00"
                            className="glass-input w-full text-sm py-2 pl-7 pr-2"
                          />
                        </div>
                        <button
                          type="button"
                          data-testid={`quick-group-item-remove-${i}`}
                          onClick={() => removeQuickGroupItemRow(i)}
                          disabled={quickGroupItems.length <= 1}
                          className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      data-testid="quick-group-add-item"
                      onClick={addQuickGroupItemRow}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-300 hover:text-white transition-colors"
                    >
                      <Plus size={14} />
                      Adicionar item
                    </button>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      data-testid="quick-group-cancel"
                      onClick={resetQuickGroup}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-white bg-gray-700/40 hover:bg-gray-700/60 rounded-lg px-3 py-2 transition-colors"
                    >
                      <X size={16} />
                      Cancelar
                    </button>
                    <button
                      type="button"
                      data-testid="quick-group-save"
                      onClick={handleCreateQuickGroup}
                      disabled={savingQuickGroup}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-60 rounded-lg px-4 py-2 transition-colors"
                    >
                      <Check size={16} />
                      {savingQuickGroup ? 'Criando...' : 'Criar e vincular'}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2 bg-gray-800/30 rounded-xl p-4">
                {additionalGroups.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">
                    Nenhum grupo criado ainda. Use o botão &quot;Novo grupo&quot; acima para criar e vincular sem sair desta tela.
                  </p>
                ) : (
                  additionalGroups.map(group => (
                    <label key={group.id} className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800/70 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        data-testid={`product-additional-group-${group.id}`}
                        checked={(formData.additional_groups || []).includes(group.id)}
                        onChange={(e) => {
                          const currentGroups = formData.additional_groups || [];
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              additional_groups: [...currentGroups, group.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              additional_groups: currentGroups.filter(id => id !== group.id)
                            });
                          }
                        }}
                        className="w-5 h-5 text-blue-500 border-gray-600 rounded focus:ring-blue-500 focus:ring-offset-gray-900"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-300">{group.name}</div>
                        <div className="text-xs text-gray-500">
                          Min: {group.min_choices} | Max: {group.max_choices}
                          {group.is_required && <span className="ml-2 text-red-400">• Obrigatório</span>}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {group.additionals_count || 0} itens
                      </div>
                    </label>
                  ))
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Selecione os grupos de adicionais que os clientes poderão escolher neste produto
              </p>
            </div>
            
            {/* Switch de Disponibilidade - Maior para mobile */}
            <div className="bg-gray-800/30 rounded-xl p-4 flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="product-available" className="text-base font-medium text-gray-300 block cursor-pointer">
                  Produto Disponível
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Clientes podem ver e pedir este produto
                </p>
              </div>
              <Switch 
                id="product-available"
                checked={formData.available}
                onCheckedChange={(checked) => setFormData({...formData, available: checked})}
                data-testid="product-available-switch"
              />
            </div>
            
            {/* Botões - Touch-friendly */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-800">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="w-full sm:w-auto px-6 py-3 text-base text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700 hover:border-gray-600"
                data-testid="product-cancel-button"
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                className="w-full sm:w-auto glass-button-primary py-3 px-8 text-base font-medium"
                data-testid="product-submit-button"
              >
                {editingProduct ? '✓ Salvar Alterações' : '+ Criar Produto'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
  );
};

export default ProductModal;